import { LDClient } from 'launchdarkly-react-client-sdk';

interface IdentifyCacheEntry {
  key: string; // composed key representing context uniqueness
  timestamp: number;
}

// Simple in-memory cache to avoid repeated identify calls within a short window.
// This avoids spamming LD when multiple components invoke the helper nearly simultaneously.
let lastIdentify: IdentifyCacheEntry | null = null;
const IDENTIFY_TTL_MS = 60_000; // 1 minute TTL (adjust as needed)

const buildContextKey = (user: { companyId: string | number }): string => `company:${user.companyId}`;

const buildLDContext = (user: { companyId: string | number }) => ({
  kind: 'case' as const,
  key: buildContextKey(user),
  companyId: user.companyId,
});

/**
 * Identifies the LaunchDarkly context for AIDI using user and case details.
 * - Safe to call multiple times; will short‑circuit if an identical identify ran recently (TTL based).
 * - Returns a Promise that resolves on callback OR after a 5s timeout to avoid UI stalls.
 */
export function identifyAidiContext(ldClient: LDClient, user: { companyId: string | number }): Promise<void> {
  return new Promise((resolve) => {
    if (!ldClient || !user) return resolve();

    const cacheKey = buildContextKey(user);
    const now = Date.now();
    if (lastIdentify && lastIdentify.key === cacheKey && now - lastIdentify.timestamp < IDENTIFY_TTL_MS) {
      return resolve(); // recent identical identify -> skip
    }

    const contextPayload = buildLDContext(user);
    let didCallCallback = false;
    const timeoutId = setTimeout(() => {
      if (!didCallCallback) resolve();
    }, 5000);

    try {
      ldClient.identify(contextPayload, undefined, () => {
        didCallCallback = true;
        clearTimeout(timeoutId);
        lastIdentify = { key: cacheKey, timestamp: Date.now() };
        resolve();
      });
    } catch (_err) {
      clearTimeout(timeoutId);
      resolve();
    }
  });
}
